import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/shared/api/client';

/**
 * Query defaults, chosen for a PWA rather than for a desktop tab.
 *
 * - **Refetch on focus and on reconnect.** A phone's browser tab survives for days in the
 *   background; the mobile clients get freshness for free by reloading the screen on every entry
 *   and have neither polling nor pull-to-refresh. This is the web's replacement for that.
 * - **A short staleTime, not zero.** Moving between tabs must not re-request the car list every
 *   time, but data older than half a minute is worth refreshing.
 * - **Do not retry what will not succeed.** A 4xx is an answer, not a hiccup: retrying a 409 or
 *   a 401 only spends the user's rate-limit budget. Retry once for transport failures and 5xx.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 1;
        },
      },
      mutations: {
        // A mutation that failed changed nothing the user can see; repeating it automatically
        // could place a second hold or create a second car. Retries here are always explicit.
        retry: false,
      },
    },
  });
}

/**
 * Query keys in one place. Screens invalidate each other's data constantly — adding a car has to
 * refresh the home screen's «Мои авто» line — and keys spelled out at each call site drift into
 * caches that never invalidate.
 */
export const queryKeys = {
  cars: ['cars'] as const,
  car: (id: string) => ['cars', id] as const,
  activeOrders: ['orders', 'active'] as const,
  /** Платежи по СБП, которые ещё не стали заказом, и недавние неудачные — блок над заявками. */
  sbpPending: ['orders', 'sbp', 'pending'] as const,
  notifications: (limit: number, offset: number) => ['notifications', limit, offset] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  /**
   * What this account still has to accept. Cached like any other server answer and never
   * persisted — the acceptance itself lives on the server, and a local «принято» flag would be
   * both forgeable and worthless as evidence (see `entities/legal/queries.ts`).
   */
  legalPending: ['legal', 'pending'] as const,
  legalDocument: (kind: string) => ['legal', 'document', kind] as const,
  legalHistory: ['legal', 'history'] as const,
  legalCurrent: ['legal', 'current'] as const,
};
